import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { MappedColumns } from 'modules/Types'
import { getFansPreview_api, importFans_api, uploadCsv_api } from 'api/brandApi'
import { BrandExistsState } from './useCurrentBrand'
import useCurrentBrand from './useCurrentBrand'

/** Interface for upload state containing CSV file and total email count */
interface Upload {
    csv?: File
    totalEmails: number
}

/** Interface for column matching configuration and source settings */
interface Match {
    source: string
    withSource: boolean
    matches: { id: string; label: string; match: string }[]
}

/** Default column mappings for CSV import */
export const DEFAULT_COLUMN_MAPPINGS = [
    { id: 'name', label: 'Name', match: 'none' },
    { id: 'lastname', label: 'Last Name', match: 'none' },
    { id: 'email', label: 'Email', match: 'none' },
    { id: 'cellphone', label: 'Cell Phone', match: 'none' },
    { id: 'source', label: 'Collected from', match: 'none' },
    { id: 'city', label: 'City', match: 'none' },
    { id: 'state', label: 'State', match: 'none' },
    { id: 'country', label: 'Country', match: 'none' },
    { id: 'ignore-column', label: 'Ignore this column', match: 'none' },
]

/** Interface for the upload emails store state and actions */
interface UploadEmailsByCsvStore {
    // State
    upload: Upload
    match: Match
    mappedColumns: MappedColumns
    isLoadingUploadEmails: boolean

    // Actions
    saveUpload: (upload: Upload) => void
    saveMatch: (match: Match) => void
    setMappedColumns: (mappedColumns: MappedColumns) => void
    setIsLoadingUploadEmails: (isLoadingUploadEmails: boolean) => void

    // Methods
    importFans: () => Promise<void>
    uploadCsv: (formData: FormData) => Promise<{ data: any, error: any }>
    getFansPreview: ({ page, first }: { page: number, first: number }) => Promise<{ data: any, error: any }>
}

const useUploadEmailsByCsv = create<UploadEmailsByCsvStore>()(
    immer((set, get) => ({
        // State
        upload: {
            csv: undefined,
            totalEmails: 0,
        },
        match: {
            source: '',
            withSource: false,
            matches: DEFAULT_COLUMN_MAPPINGS,
        },
        mappedColumns: {},
        isLoadingUploadEmails: false,

        // Actions
        saveUpload: (upload) => set((state) => { state.upload = upload }),
        saveMatch: (match) => set((state) => { state.match = match }),
        setMappedColumns: (mappedColumns) => set((state) => { state.mappedColumns = mappedColumns }),
        setIsLoadingUploadEmails: (isLoadingUploadEmails) => set((state) => { state.isLoadingUploadEmails = isLoadingUploadEmails }),

        // Methods
        importFans: async () => {
            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState;
            const { setIsLoadingUploadEmails, mappedColumns, match } = get()

            try {
                setIsLoadingUploadEmails(true);
                const { error } = await importFans_api(currentBrand.id, {
                    mappedHeaders: mappedColumns,
                    source: match.source,
                });
                if (error) {
                    throw new Error('Error importing fans');
                }
            } catch (err) {
                console.error('Error importing fans:', err);
                throw err
            } finally {
                setIsLoadingUploadEmails(false);
            }
        },
        uploadCsv: async (formData) => {
            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState;
            const { setIsLoadingUploadEmails } = get()

            try {
                setIsLoadingUploadEmails(true);
                const res = await uploadCsv_api(currentBrand.id, formData);
                return res as { data: number, error: boolean };
            } catch (err) {
                console.error('Error uploading CSV:', err);
                throw err
            } finally {
                setIsLoadingUploadEmails(false);
            }
        },
        getFansPreview: async ({ page, first }: { page: number, first: number }) => {
            const { currentBrand } = useCurrentBrand.getState() as BrandExistsState;
            const { setIsLoadingUploadEmails, mappedColumns, match } = get()

            try {
                setIsLoadingUploadEmails(true);
                const res = await getFansPreview_api(currentBrand.id, {
                    mappedHeaders: mappedColumns,
                    source: match.source,
                    page,
                    first,
                });
                return res as { data: any, error: any };
            } catch (err) {
                console.error('Error getting fans preview:', err);
                throw err
            } finally {
                setIsLoadingUploadEmails(false);
            }
        }
    }))
)

export default useUploadEmailsByCsv;
